import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ExternalLink, Library, Share2 } from "lucide-react";
import { ShareCardModal } from "@/components/ShareCardModal";
import { HeadlineGuess } from "@/components/HeadlineGuess";
import api from "@/lib/api";
import { track } from "@/lib/analytics";

export const HeadlineShareButton = ({ h, slug, testid = "hof-share" }) => {
  const [open, setOpen] = useState(false);
  const shareUrl = `${window.location.origin}/oddlytrue-news/${slug || ""}`.replace(/\/$/, "");
  const label = h.verified ? `Verified real headline${h.year ? ` · ${h.year}` : ""}` : `Classic headline${h.year ? ` · ${h.year}` : ""}`;
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 h-9 bg-ink text-white text-[12px] font-semibold hover:bg-gold hover:text-ink transition-colors">
        <Share2 className="w-3.5 h-3.5" /> Share this headline
      </button>
      <ShareCardModal open={open} onClose={() => setOpen(false)} heading="Share this strange-but-true headline" analyticsName="headline_card" testPrefix="headline-card" defaultTheme="newsprint"
        shareUrl={shareUrl} shareText={`${h.verified ? "Real headline" : "Classic headline"}${h.year ? ` from ${h.year}` : ""}: "${h.headline}" — OddlyTrue News, strange-but-true vintage headlines`}
        card={{ badge: label, greeting: h.newspaper || "OddlyTrue News · Strange-But-True", title: h.headline, body: h.snippet || `A ${h.verified ? "real" : "classic"} headline${h.newspaper ? ` printed in ${h.newspaper}` : ""}${h.year ? ` in ${h.year}` : ""}. Source: ${h.source || "newspaper archive"}.`,
          dateLabel: h.date || (h.year ? String(h.year) : ""), footer: "A real weird headline every day → oddlytrue.co" }} />
    </>
  );
};

export const HallOfFameCard = ({ h, testid = "hof-card", slug, hideYear = false, onReveal }) => {
  if (!h) return null;
  return (
    <article data-testid={testid} className="relative overflow-hidden border-2 border-ink bg-[#f3e9cf] text-ink shadow-[6px_6px_0_#111827]"
      style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(120,90,30,0.14), transparent 45%), radial-gradient(circle at 85% 90%, rgba(120,90,30,0.18), transparent 40%)" }}>
      <div className="px-6 pt-5 pb-2 border-b-2 border-ink/70 flex flex-wrap items-center justify-between gap-2">
        <p className="font-headline text-[13px] font-bold uppercase tracking-[0.25em]">{h.newspaper || "From the archives"}</p>
        <span data-testid={`${testid}-badge`} className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${h.verified ? "bg-ink text-gold" : "border border-ink text-ink"}`}>
          <Award className="w-3 h-3" /> {h.verified ? "Verified Real" : "Classic headline"}{h.year && !hideYear ? ` ${h.year}` : ""}
        </span>
        {hideYear && <button type="button" data-testid={`${testid}-reveal`} onClick={onReveal} className="w-full text-left text-[10px] font-bold uppercase tracking-[0.15em] text-ink/70 hover:text-ink hover:underline">Year hidden — guess it in the game below, or tap to reveal</button>}
      </div>
      <div className="px-6 py-6">
        <h3 data-testid={`${testid}-headline`} className="font-headline text-[1.5rem] md:text-[1.8rem] lg:text-[2.4rem] font-bold leading-[1.12] tracking-tight">{h.headline}</h3>
        {h.snippet && <p data-testid={`${testid}-snippet`} className="mt-4 max-w-2xl text-[15px] leading-relaxed text-ink/85 first-letter:font-vintage first-letter:text-4xl first-letter:float-left first-letter:mr-2 first-letter:leading-[0.9]">{h.snippet}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-wider text-ink/70">
          {h.date && !hideYear && <span>{h.date}</span>}
          {h.source && <span>Source: {h.source}</span>}
          {h.source_url && <a href={h.source_url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-source-link`} className="inline-flex items-center gap-1 text-ink underline font-semibold hover:underline normal-case tracking-normal">View clipping source <ExternalLink className="w-3 h-3" /></a>}
          <span className="ml-auto"><HeadlineShareButton h={h} slug={slug} testid={`${testid}-share`} /></span>
        </div>
      </div>
    </article>
  );
};

/** Share the clipping - or, while the year is hidden, share the guess-the-year challenge instead of the answer. */
const ClippingShare = ({ c, paperName, challenge, testid }) => {
  const url = `${window.location.origin}/#hall-of-fame`;
  const text = challenge
    ? `Can you guess the year this page of ${paperName} was printed? Play today's OddlyTrue challenge:`
    : `A page from ${paperName}, ${c.year} — today's vintage clipping on OddlyTrue.`;
  const share = (channel) => track("clipping_share", { channel, challenge: !!challenge, category: "share" });
  const a = "inline-flex items-center h-7 px-2.5 rounded-full border border-ink/20 bg-white text-[11px] font-bold text-ink-muted hover:border-gold hover:text-gold transition-colors";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`${testid}-share`}>
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-muted mr-1"><Share2 className="w-3 h-3" /> {challenge ? "Share the challenge" : "Share this page"}</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noopener noreferrer" onClick={() => share("whatsapp")} data-testid={`${testid}-share-whatsapp`} className={a}>WhatsApp</a>
      <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" onClick={() => share("x")} data-testid={`${testid}-share-x`} className={a}>X</a>
    </div>
  );
};

export const ClippingCard = ({ c, testid = "clipping-card", hideYear = false, onReveal }) => {
  if (!c) return null;
  const paperName = (c.newspaper || "").replace(/,\s*[A-Z][a-z]+\.? \d{1,2}, \d{4}\s*$/, "");
  return (
    <article data-testid={testid} className="flex flex-col sm:flex-row gap-4 border border-ink/30 bg-[#fbf8f1] p-4">
      {c.image && <a href={hideYear ? undefined : c.url} target="_blank" rel="noopener noreferrer" className="shrink-0"><img src={c.image} alt={paperName || "Newspaper page"} loading="lazy" className={`w-full sm:w-40 h-48 object-cover border border-ink/20 sepia-[25%] ${hideYear ? "object-center blur-[1.5px]" : "object-top"}`} /></a>}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-ink-muted"><Library className="w-3.5 h-3.5 text-gold" /> Vintage clipping · {hideYear ? <button type="button" onClick={onReveal} data-testid={`${testid}-reveal`} className="font-mono text-gold hover:underline" title="Reveal the year">????</button> : c.year}</div>
        <h4 className="mt-1 font-vintage text-xl font-bold text-ink leading-snug">{hideYear ? paperName : c.newspaper}</h4>
        <p className="mt-1 text-[13px] text-ink-muted">{c.page ? `Page ${c.page}` : "Page"}{c.keyword ? ` · mentions "${c.keyword}"` : ""}{c.same_day && !hideYear ? " · printed on this very day" : ""}</p>
        {c.snippet && <p className="mt-2 text-[13px] text-ink/80 leading-relaxed">“{c.snippet}…”</p>}
        {!hideYear && <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-gold hover:underline">Read the page at the Library of Congress <ExternalLink className="w-3 h-3" /></a>}
        <ClippingShare c={c} paperName={paperName} challenge={hideYear} testid={testid} />
        <p className="mt-1 text-[10px] text-ink-muted">Image and text {c.attribution}.</p>
      </div>
    </article>
  );
};

export const NewsAttribution = ({ news }) => (
  <p data-testid="oddlytrue-news-attribution" className="mt-5 text-[11px] text-ink-muted border-t border-ink/10 pt-3">
    {news.attribution.halloffame} {news.attribution.wikipedia} Clippings {news.attribution.loc}.
  </p>
);

export const HallOfFameReel = ({ ddmm }) => {
  const [news, setNews] = useState(null);
  const [failed, setFailed] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [reveal, setReveal] = useState(false);
  const dateKey = ddmm ? `${new Date().getUTCFullYear()}-${ddmm.slice(3, 5)}-${ddmm.slice(0, 2)}` : new Date().toISOString().slice(0, 10);
  const clip = news?.vintageClipping;
  const clipName = (clip?.newspaper || "").replace(/,\s*[A-Z][a-z]+\.? \d{1,2}, \d{4}\s*$/, "");
  const target = news?.hallOfFame?.year ? { kind: "hof", year: news.hallOfFame.year, text: news.hallOfFame.headline }
    : clip?.year && clipName ? { kind: "clipping", year: clip.year, text: clipName } : null;
  const path = ddmm ? `/oddlytrue-news/${parseInt(ddmm.slice(3, 5), 10)}/${parseInt(ddmm.slice(0, 2), 10)}` : "/oddlytrue-news/today";
  useEffect(() => {
    setNews(null); setFailed(false); setAnswered(false);
    api.get(path).then((r) => setNews(r.data)).catch(() => setFailed(true));
  }, [path]);
  if (failed) return null;
  return (
    <section id="hall-of-fame" data-testid="halloffame-reel" className="mt-10 border-t-4 border-double border-ink pt-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-gold" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Strange but true headlines of the day</p>
          </div>
          <h2 data-testid="halloffame-title" className="mt-1 font-vintage text-3xl md:text-4xl font-bold text-ink tracking-tight">OddlyTrue News Stand</h2>
        </div>
        {news && <Link to={`/oddlytrue-news/${news.slug}`} data-testid="halloffame-more-link" className="text-[13px] font-semibold text-gold hover:underline">Today's full edition →</Link>}
      </div>
      {!news ? (
        <div className="h-56 bg-[#f3e9cf] border-2 border-ink/20 animate-pulse" />
      ) : (
        <>
          <HallOfFameCard h={news.hallOfFame} slug={news.slug} hideYear={target?.kind === "hof" && !answered} onReveal={() => setReveal(true)} />
          {target?.kind === "clipping" && <div className="mt-4"><ClippingCard c={clip} testid="halloffame-clipping" hideYear={!answered} onReveal={() => setReveal(true)} /></div>}
          {target && <HeadlineGuess target={target} dateKey={dateKey} onAnswered={() => setAnswered(true)} revealRequested={reveal} />}
          <NewsAttribution news={news} />
        </>
      )}
    </section>
  );
};
